package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.AstNode
import top.wcpe.allincore.dsl.compiler.ast.DslMode
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.MigrationHint
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 顶层语法分析器。
 *
 * 持有 token 流游标，并通过 internal 工具方法供同包 [ScreenParser]/[StateBlockParser]/
 * [ActionBlockParser]/[ElementBlockParser]/[ExpressionParser] 复用。
 *
 * <p>可选注入的 [diagnostics] 用于上报解析期非致命警告（如已废弃语法形式）。
 * Pipeline 会注入与语义阶段同一个 collector，使 warning 与 error 一起在 [CompileResult.diagnostics] 中返回。
 * 单测构造器调用未传时为 null，warning 简单丢弃，保持兼容。</p>
 */
class Parser(
    internal val tokens: List<Token>,
    internal val screenId: String = "inline",
    internal val diagnostics: DiagnosticCollector? = null,
    inheritedDslMode: DslMode? = null,
) {

    private var index = 0

    /**
     * 局部变量作用域栈（C1）。
     *
     * 每个元素是当前块（action/callback/if/while/try）声明的 `var` 名集合。
     * 名称解析采用就近原则：从栈顶向栈底依次查找。
     * 由 ActionBlockParser 在进入/离开 block 时 push/pop。
     */
    private val localStack: ArrayDeque<MutableSet<String>> = ArrayDeque()

    /** 进入一个新的块级作用域。 */
    internal fun pushBlockScope() {
        localStack.addLast(linkedSetOf())
    }

    /** 离开当前块级作用域。 */
    internal fun popBlockScope() {
        check(localStack.isNotEmpty()) { "popBlockScope 时作用域栈为空" }
        localStack.removeLast()
    }

    /** 在当前栈顶作用域声明一个局部变量；重复声明返回 false。 */
    internal fun declareLocal(name: String): Boolean {
        check(localStack.isNotEmpty()) { "declareLocal 时无活动作用域" }
        return localStack.last().add(name)
    }

    /** 查询任意已可见作用域中是否存在该局部变量。 */
    internal fun isLocal(name: String): Boolean {
        for (i in localStack.indices.reversed()) {
            if (name in localStack[i]) return true
        }
        return false
    }

    /**
     * source 形参作用域栈（Phase B.4）。
     *
     * 与 [localStack] 平行的栈，专门记录容器 source 形参（`column "x" { item -> ... }`
     * 中的 `item`）。两者拆分的原因：
     *  - `var` 类局部变量在运行时通过 [EvalContext.getVar]（`LocalRef` 解析）访问；
     *  - source 形参在运行时通过 [EvalContext.getLocals]（`IdentRef` 解析）访问，
     *    由 `IrInterpreter.instantiateTemplate*` 写入 `newLocals`。
     *
     * 因此解析期把 source 形参当作"允许裸引用为 IdentRef"的名字集合，而非 `LocalRef`，
     * 与 [Parser.localStack] 隔离避免错把它们升格为 LocalRef 引发运行时未声明变量错误。
     *
     * 由 [ElementBlockParser.withSourceParamScope] 在进入/离开容器元素体时 push/pop。
     */
    private val sourceParamStack: ArrayDeque<MutableSet<String>> = ArrayDeque()

    /** 进入一个新的 source 形参作用域。 */
    internal fun pushSourceParamScope() {
        sourceParamStack.addLast(linkedSetOf())
    }

    /** 离开当前 source 形参作用域。 */
    internal fun popSourceParamScope() {
        check(sourceParamStack.isNotEmpty()) { "popSourceParamScope 时作用域栈为空" }
        sourceParamStack.removeLast()
    }

    /** 在当前栈顶 source 形参作用域内注册一个形参名。 */
    internal fun declareSourceParam(name: String) {
        check(sourceParamStack.isNotEmpty()) { "declareSourceParam 时无活动作用域" }
        sourceParamStack.last().add(name)
    }

    /** 查询任意已可见作用域中是否存在该 source 形参名。 */
    internal fun isSourceParam(name: String): Boolean {
        for (i in sourceParamStack.indices.reversed()) {
            if (name in sourceParamStack[i]) return true
        }
        return false
    }

    /**
     * 顶层 fun 名集合（C4，screen 级 forward reference）。
     *
     * 由 [TopLevelParser] / [ScreenParser] 在解析前预扫一遍 tokens，把所有 `fun NAME` 写入此集合；
     * 之后 ExpressionParser 在解析 `name(args)` 时若 name 命中此集合（且未被局部 var 遮蔽），
     * 即产出 `CallExpr(FunRefExpr(name), args)`，从而支持调用书写在声明之前的 fun。
     *
     * 4b660e9 起：还包含 .allin 文件级（screen 之外）的 fun 名，与 screen 级共享 forward reference。
     */
    internal val screenFuns: MutableSet<String> = linkedSetOf()
    internal fun isScreenFun(name: String): Boolean = name in screenFuns

    /** while 嵌套深度，break/continue 仅在 >0 时合法。 */
    private var loopDepth: Int = 0

    internal fun enterLoop() { loopDepth++ }
    internal fun exitLoop() {
        check(loopDepth > 0) { "exitLoop underflow" }
        loopDepth--
    }
    internal fun inLoop(): Boolean = loopDepth > 0

    /** action / callback 体嵌套深度，return 仅在 >0 时合法。 */
    private var returnDepth: Int = 0
    internal fun enterReturnable() { returnDepth++ }
    internal fun exitReturnable() {
        check(returnDepth > 0) { "exitReturnable underflow" }
        returnDepth--
    }
    internal fun inReturnable(): Boolean = returnDepth > 0

    /**
     * 当前是否正在解析 `await net.request(...)`：
     * 用于允许 `net.request(...)` 在 await 直接子表达式位置省略 callback。
     */
    private var awaitDepth: Int = 0
    internal fun enterAwait() { awaitDepth++ }
    internal fun exitAwait() {
        check(awaitDepth > 0) { "exitAwait underflow" }
        awaitDepth--
    }
    internal fun inAwait(): Boolean = awaitDepth > 0

    /** 当前是否处于 component 文件解析中（影响 children 占位的合法性）。 */
    internal var inComponentFile: Boolean = false

    /** 当前文件的 DSL 模式；多文件编译可让无头组件继承所属 screen 模式。 */
    internal var currentDslMode: DslMode = inheritedDslMode ?: DslMode.LEGACY

    internal fun isStrictMode(): Boolean = currentDslMode == DslMode.STRICT

    internal fun rejectReservedUserIdentifier(name: String, position: SourcePosition) {
        if (name.startsWith("__allin") || name.startsWith("$")) {
            fail(DslError.AUI_V2_RESERVED_PREFIX, position, name)
        }
    }

    /**
     * 通用顶层解析入口：根据首个关键字分发到 [ScreenParser] 或 [ComponentParser]。
     *
     * 返回 [ScreenAst] 或 [ComponentAst][top.wcpe.allincore.dsl.compiler.ast.ComponentAst]。
     */
    fun parseTopLevel(): AstNode = TopLevelParser(this).parse()

    /**
     * 兼容解析入口：消费整个 token 流并返回 [ScreenAst]。
     *
     * 内部委托 [parseTopLevel]，若结果不是 [ScreenAst] 则抛出异常。
     * 已有调用方（Pipeline、测试）可继续使用此方法而无需改动。
     */
    fun parse(): ScreenAst {
        val ast = parseTopLevel()
        if (ast is ScreenAst) return ast
        fail("本入口仅接受 screen 文件，请使用 parseTopLevel()")
    }

    /**
     * 预扫描 token 流，把所有文件级 / screen 级的 fun 名字填入 [screenFuns]。
     * <p>
     * 用于支持 fun 之间的 forward reference：
     *  - screen 前声明的顶层 fun 可以调用 screen 后声明的顶层 fun；
     *  - screen 内 fun 可以调用同 screen 内尚未声明的 fun、以及前后任意顶层 fun。
     * </p>
     * <p>
     * 精确识别两种位置（不接受任何近似）：
     *  - 文件顶层（depth == 0）：识别为顶层 fun
     *  - screen 块**直接子**位置（screen `{` 内，depth == 1）：识别为 screen 级 fun
     * </p>
     * <p>
     * 通过 {@code screenDepthBase} 显式记录"进入 screen 块时的 depth"来精准区分
     * "screen 块直接子" vs "screen 之后的顶层 fun body 内"。后者旧实现会误登记，
     * 新实现明确拒绝。
     * </p>
     */
    internal fun prescanFileLevelFuns() {
        var depth = 0
        // -1 表示尚未进入 / 已离开 screen / hud 块；非 -1 表示当前在主块内、且其 LBRACE 时 depth 值
        var screenDepthBase = -1
        var pendingScreen = false
        var i = 0
        while (i < tokens.size) {
            val t = tokens[i]
            when (t.type) {
                // Stage 11：KW_SCREEN 与 KW_HUD 在预扫层等价处理（两者块内 fun 都需登记到 screenFuns）
                TokenType.KW_SCREEN, TokenType.KW_HUD -> {
                    if (depth == 0 && screenDepthBase == -1) pendingScreen = true
                }
                TokenType.LBRACE -> {
                    if (pendingScreen) {
                        screenDepthBase = depth
                        pendingScreen = false
                    }
                    depth++
                }
                TokenType.RBRACE -> {
                    if (depth > 0) depth--
                    // 离开主块（回到主块 LBRACE 之前的 depth）
                    if (screenDepthBase != -1 && depth == screenDepthBase) {
                        screenDepthBase = -1
                    }
                }
                TokenType.KW_FUN -> {
                    val isFileTop = depth == 0
                    val isScreenDirectChild =
                        screenDepthBase != -1 && depth == screenDepthBase + 1
                    if (isFileTop || isScreenDirectChild) {
                        var k = i + 1
                        while (k < tokens.size && tokens[k].type == TokenType.NEWLINE) k++
                        if (k < tokens.size && tokens[k].type == TokenType.IDENTIFIER) {
                            screenFuns += tokens[k].lexeme
                        }
                    }
                }
                TokenType.IDENTIFIER -> {
                    // M5-e：兼容 `suspend fun NAME` 前缀（suspend 为软关键字，词法层为 IDENTIFIER）。
                    // 仅文件顶层或 screen / hud 块直接子位置需要登记。
                    if (t.lexeme == "suspend") {
                        val isFileTop = depth == 0
                        val isScreenDirectChild =
                            screenDepthBase != -1 && depth == screenDepthBase + 1
                        if (isFileTop || isScreenDirectChild) {
                            var k = i + 1
                            while (k < tokens.size && tokens[k].type == TokenType.NEWLINE) k++
                            if (k < tokens.size && tokens[k].type == TokenType.KW_FUN) {
                                var j = k + 1
                                while (j < tokens.size && tokens[j].type == TokenType.NEWLINE) j++
                                if (j < tokens.size && tokens[j].type == TokenType.IDENTIFIER) {
                                    screenFuns += tokens[j].lexeme
                                }
                            }
                        }
                    }
                }
                else -> Unit
            }
            i++
        }
    }

    // ===== 内部工具：暴露给同包 *Parser =====

    /** 向后看 [offset] 个 token；越界返回最后一枚（通常是 EOF）。 */
    internal fun peek(offset: Int = 0): Token {
        val i = index + offset
        return if (i >= tokens.size) tokens.last() else tokens[i]
    }

    /** 当前 token。 */
    internal fun current(): Token = peek(0)

    /** 最近消费的 token；尚未消费时返回首枚 token。 */
    internal fun previous(): Token = tokens[(index - 1).coerceAtLeast(0)]

    /** 将节点起点扩展到最近消费 token 的实际语法终点。 */
    internal fun completePosition(start: SourcePosition, end: Token = previous()): SourcePosition =
        if (start.sourceId == end.range.sourceId) start.endingAt(end.range.end) else start

    /** 返回当前 token 并将游标前进一格。 */
    internal fun advance(): Token {
        val t = current()
        if (index < tokens.size - 1) index++
        return t
    }

    /** 当前 token 类型是否为 [type]。 */
    internal fun check(type: TokenType): Boolean = current().type == type

    /** 若当前 token 类型为 [type]，则前进并返回 true；否则返回 false。 */
    internal fun match(type: TokenType): Boolean {
        if (check(type)) {
            advance()
            return true
        }
        return false
    }

    /** 期望当前 token 为 [type]，否则抛 [ParseException]。 */
    internal fun expect(type: TokenType, hint: String = ""): Token {
        if (!check(type)) {
            val cur = current()
            val msg = if (hint.isEmpty()) {
                "期望 $type，实际为 ${cur.type}（'${cur.lexeme}'）"
            } else {
                "$hint：期望 $type，实际为 ${cur.type}（'${cur.lexeme}'）"
            }
            throw ParseException(msg, cur.position)
        }
        return advance()
    }

    /** 跳过连续 NEWLINE。 */
    internal fun skipNewlines() {
        while (check(TokenType.NEWLINE)) advance()
    }

    /**
     * 消费一个可选 `-` 前缀的数字字面量并返回其 double 值。
     *
     * 同时被 [ExpressionParser.parseNegativeNumber] / [AnimationBlockParser] 复用，
     * 避免"可选负号 + NUMBER"的解析逻辑分散在多处。
     *
     * @param numberHint 期望数字时若失败用于诊断的提示信息
     */
    internal fun consumeSignedDouble(numberHint: String): Double {
        val sign = if (match(TokenType.MINUS)) -1.0 else 1.0
        val numTok = expect(TokenType.NUMBER, numberHint)
        return sign * (numTok.literalValue as Number).toDouble()
    }

    /** 抛出 [ParseException]。 */
    internal fun fail(message: String, position: SourcePosition = current().position): Nothing {
        throw ParseException(message, position)
    }

    /**
     * 抛出 [ParseException]，消息由 [DslError] 模板填充并自动加上 `[DSL_EXXX] ` 前缀。
     *
     * 该重载用于在解析阶段直接产出与诊断码挂钩的异常，错误信息生成方式与
     * [DiagnosticCollector.error] 重载保持一致。CompilationPipeline 仍以
     * [DslError.E002_PARSE_ERROR] 转报，前缀使解析期错误码在透传后仍可识别。
     */
    internal fun fail(
        err: DslError,
        position: SourcePosition = current().position,
        vararg args: Any?,
    ): Nothing {
        throw ParseException("[${err.code}] ${err.format(*args)}", position, err)
    }

    /**
     * 上报解析期 WARNING 级诊断；当 [diagnostics] 为空（构造器未注入）时静默丢弃。
     *
     * 用法上专为非致命的已废弃语法识别预留，例如 `data "Name" { ... }` 的字符串类型名形式。
     * 与 [fail] 不同，本方法不打断解析流程。
     */
    internal fun warn(
        err: DslError,
        position: SourcePosition,
        vararg args: Any?,
    ) {
        diagnostics?.warning(err, position, *args)
    }

    internal fun warnMigration(
        err: DslError,
        position: SourcePosition,
        migration: MigrationHint,
        vararg args: Any?,
    ) {
        diagnostics?.warning(err, position, migration, *args)
    }
}
